import { useForm, Controller, useFieldArray } from "react-hook-form";
import { IItineranteFormRHF } from "../../../types/itinerante";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ibge } from "../../../services/Ibge";
import { useCallback, useEffect, useState } from "react";
import { formatarMoedaBRL, maskCep, maskTime } from "../../../utils/masks";
import { toast } from "react-toastify";
import { ViaCep } from "../../../services/ViaCep";
import { useContextSite } from "../../../context/Context";
import { ISelectOptions } from "../../../types/inputs";
import { Itinerante } from "../../../services/Itinerante";
import { Colaborador } from "../../../services/Colaborador";
import { TipoColaboradorEnum } from "../../../enums/tipoColaborador";
import { Delivery } from "../../../services/Delivery";
import { Conta } from "../../../services/Conta";
import { ItemCustoForm } from "../../../types/conta";

const enderecoSchema = z.object({
  bairro: z.string().min(1, "Campo obrigatorio"),
  cep: z.string().min(1, "Campo obrigatorio"),
  cidade: z.string().min(1, "Campo obrigatorio"),
  complemento: z.string().optional(),
  logradouro: z.string().min(1, "Campo obrigatorio"),
  numero: z.string().min(1, "Campo obrigatorio"),
  uf: z.string().min(1, "Campo obrigatorio"),
});

const colaboradoresSchema = z.object({
  value: z.string().min(1, "Campo obrigatorio"),
  label: z.string().min(1, "Campo obrigatorio"),
});

const itemCustomListSchema = z.object({
  uuidConta: z.string().optional(),
  quantidade: z.number().optional(),
  valorUnitario: z.number().optional(),
});

const schema = z.object({
  dataRealizacao: z.string().min(1, "Campo obrigatorio"),
  horarioFinal: z.string().min(5, "O formato do horario deve ser HH:MM"),
  horarioFinalAlmoco: z
    .string()
    .min(5, "O formato do horario deve ser HH:MM")
    .or(z.literal("")),
  horarioInicial: z.string().min(5, "O formato do horario deve ser HH:MM"),
  horarioInicialAlmoco: z
    .string()
    .min(5, "O formato do horario deve ser HH:MM")
    .or(z.literal("")),
  itemCustoList: z.array(itemCustomListSchema).optional(),
  quantidadeVagas: z.string().min(1, "Campo obrigatorio"),
  uuidColaboradores: z
    .array(colaboradoresSchema)
    .nonempty("Deve conter pelo menos um colaborador"),
  tempoMedio: z.string().min(5, "O formato do horario deve ser HH:MM"),
  uuidDelivery: z.string().min(1, "Campo obrigatorio"),
  endereco: enderecoSchema,
});

export const useFormItinerantRegister = () => {
  const { setIsLoad } = useContextSite();
  const [ufOptions, setUfOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [cidadeTemp, setCidadeTemp] = useState("");
  const [cidadesOptions, setCidadesOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [unidades, setUnidades] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );

  const [contasOptions, setContasOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );

  const [vistoriadores, setVistoriadores] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [active, setActive] = useState(false);
  const [planejamentoOrcamentario, setPlanejamentoOrcamentario] =
    useState<ItemCustoForm>({} as ItemCustoForm);

  const {
    register,
    watch,
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IItineranteFormRHF>({
    defaultValues: {
      dataRealizacao: "",
      horarioFinal: "",
      horarioFinalAlmoco: "",
      horarioInicial: "",
      horarioInicialAlmoco: "",
      quantidadeVagas: 0 || null,
      uuidColaboradores: [],
      itemCustoList: [],
      tempoMedio: "",
      uuidDelivery: "",
      endereco: {
        bairro: "",
        cep: "",
        cidade: "",
        complemento: "",
        logradouro: "",
        numero: "",
        uf: "",
      },
    },
    resolver: zodResolver(schema),
    mode: "all",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "uuidColaboradores",
  });
  const [totalOrcamento, setTotalOrcamento] = useState(0);
  const getUfs = useCallback(() => {
    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((item) => ({
          value: item.sigla,
          label: item.sigla,
          element: item,
        }));

        setUfOptions(options);
      })
      .catch((erro) => toast.error("Erro ao requisitar as UFs"));
  }, []);

  const getContas = useCallback(() => {
    Conta.get({ ativo: true, size: 100 })
      .then(({ data }) => {
        const options = data.content.map((i) => ({
          value: i.uuid,
          label: i.nome,
          element: i,
        }));

        setContasOptions(options);
      })
      .catch(() => {
        console.log("Erro ao requisitar as contas");
      });
  }, []);
  const getUnidades = useCallback(() => {
    Delivery.get()
      .then(({ data }) => {
        const unidadesUnicas = data.content.map((i) =>
          JSON.stringify({
            label: i?.cidade,
            value: i?.uuid,
          })
        );

        const unidadesOptions = unidadesUnicas.map((i) => JSON.parse(i));

        setUnidades(unidadesOptions);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      );
  }, []);

  useEffect(() => {
    getUfs();
    getUnidades();
    getContas();
  }, []);

  function getVistoriadores() {
    Colaborador.listarPorDelivery({
      uuidDelivery: watch("uuidDelivery"),
      tipo: TipoColaboradorEnum.VISTORIADOR,
    }).then(({ data }) => {
      const options = data.map((i) => ({
        value: i.uuid,
        label: i.nome,
        element: i,
      }));

      setVistoriadores(options);
    });
  }

  function handleRemoveItemCusto(item: ItemCustoForm) {
    const updatedList = [...(watch("itemCustoList") || [])];
    const index = updatedList.findIndex(
      (i) =>
        i.quantidade === item.quantidade &&
        i.uuidConta === item.uuidConta &&
        i.valorUnitario === item.valorUnitario
    );

    if (index > -1) {
      updatedList.splice(index, 1);
      setValue("itemCustoList", updatedList);
    }
  }

  function handleAddItemCusto() {
    if (
      !planejamentoOrcamentario?.uuidConta ||
      !planejamentoOrcamentario?.quantidade ||
      !planejamentoOrcamentario?.valorUnitario
    ) {
      return toast.error("Preencha todos os campos para adicionar o item");
    }

    const updatedList = [
      ...(watch("itemCustoList") || []),
      planejamentoOrcamentario,
    ];

    setValue("itemCustoList", updatedList);

    setPlanejamentoOrcamentario({
      quantidade: 0,
      uuidConta: null,
      valorUnitario: 0,
    } as ItemCustoForm);
  }

  function handleCep() {
    if (watch("endereco.cep").length === 9) {
      setIsLoad(true);
      setTimeout(() => {
        ViaCep.get(watch("endereco.cep"))
          .then(({ data }) => {
            setValue("endereco.logradouro", data.logradouro);
            setValue("endereco.bairro", data.bairro);
            // setValue("endereco.cidade", data.localidade);
            setValue("endereco.uf", data.uf);
            setCidadeTemp(data.localidade);
          })
          .catch((erro) => toast.error("Cep não encontrado"))
          .finally(() => setIsLoad(false));
      }, 1000);
    }
  }

  useEffect(() => {
    if (watch("endereco.uf")) {
      setValue("endereco.cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("endereco.uf") })
        .then(({ data }) => {
          const options = data.map((item) => ({
            value: item.nome,
            label: item.nome,
            element: item,
          }));
          setCidadesOptions(options);
          setValue("endereco.cidade", cidadeTemp);
        })
        .then(() => {
          setCidadeTemp("");
        })
        .catch((erro) => toast.error("Erro ao requisitar a lista de cidades"));
    }
  }, [watch("endereco.uf")]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(watch("endereco.cep")));
  }, [watch("endereco.cep")]);

  useEffect(() => {
    if (watch("horarioInicial")) {
      setValue("horarioInicial", maskTime(watch("horarioInicial")));
    }
  }, [watch("horarioInicial")]);

  useEffect(() => {
    if (watch("horarioFinal")) {
      setValue("horarioFinal", maskTime(watch("horarioFinal")));
    }
  }, [watch("horarioFinal")]);

  useEffect(() => {
    if (watch("tempoMedio")) {
      setValue("tempoMedio", maskTime(watch("tempoMedio")));
    }
  }, [watch("tempoMedio")]);

  useEffect(() => {
    const total = watch("itemCustoList")?.reduce((acc, item) => {
      const valorUnitario = Number(item.valorUnitario);
      const quantidade = Number(item.quantidade);
      return acc + valorUnitario * quantidade;
    }, 0);

    setTotalOrcamento(total || 0);
  }, [watch("itemCustoList")]);

  useEffect(() => {
    if (!active) {
      setValue("horarioInicialAlmoco", "");
      setValue("horarioFinalAlmoco", "");
    }
  }, [active]);

  useEffect(() => {
    if (watch("horarioFinalAlmoco")) {
      setValue("horarioFinalAlmoco", maskTime(watch("horarioFinalAlmoco")));
    }
  }, [watch("horarioFinalAlmoco")]);

  useEffect(() => {
    if (watch("horarioInicialAlmoco")) {
      setValue("horarioInicialAlmoco", maskTime(watch("horarioInicialAlmoco")));
    }
  }, [watch("horarioInicialAlmoco")]);

  useEffect(() => {
    if (watch("uuidDelivery")) {
      getVistoriadores();
    }
  }, [watch("uuidDelivery")]);

  return {
    Controller,
    register,
    control,
    handleSubmit,
    ufOptions,
    handleCep,
    cidadesOptions,
    active,
    setActive,
    fields,
    append,
    remove,
    unidades,
    vistoriadores,
    watch,
    errors,
    contasOptions,
    planejamentoOrcamentario,
    setPlanejamentoOrcamentario,
    handleAddItemCusto,
    getValues,
    totalOrcamento,
    handleRemoveItemCusto,
  };
};
